import React, { useState, useEffect } from 'react';
import {
    Bell, Check, Trash2, Calendar, AlertTriangle,
    Info, CheckCircle2, ArrowRight,
    CheckSquare
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import NotificationService from '../../services/NotificationService';
import usePageView from '../../hooks/usePageView';
import { ANALYTICS_EVENTS } from '../../constants/analyticsEvents';

const Notifications = () => {
    usePageView(ANALYTICS_EVENTS.NOTIFICATIONS_VIEWED);
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // all, unread

    useEffect(() => {
        fetchNotifications();
        // Re-fetch when the filter changes; fetchNotifications reads `filter`
        // internally. Adding fetchNotifications to deps would either loop or
        // require a useCallback wrap with the same deps anyway.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    const fetchNotifications = async () => {
        try {
            setLoading(true);
            const data = await NotificationService.getNotifications(
                50,
                filter === 'unread'
            );

            if (data && data.notifications) {
                setNotifications(data.notifications);
                setUnreadCount(data.unread_count);
            }
        } catch (error) {
            console.error('Failed to fetch notifications', error);
        } finally {
            setLoading(false);
        }
    };

    const handleMarkRead = async (id, e) => {
        e.stopPropagation();
        try {
            const result = await NotificationService.markAsRead(id);
            if (result.success) {
                // Update local state to reflect read status
                setNotifications(notifications.map(n =>
                    n.id === id ? { ...n, is_read: true } : n
                ));
                setUnreadCount(result.unread_count);
            }
        } catch (error) {
            console.error('Failed to mark as read', error);
        }
    };

    const handleDismiss = async (id, e) => {
        e.stopPropagation();
        try {
            const result = await NotificationService.dismiss(id);
            if (result.success) {
                // Remove from list
                setNotifications(notifications.filter(n => n.id !== id));
                setUnreadCount(result.unread_count);
            }
        } catch (error) {
            console.error('Failed to dismiss', error);
        }
    };

    const handleMarkAllRead = async () => {
        try {
            const result = await NotificationService.markAllAsRead();
            if (result.success) {
                setNotifications(notifications.map(n => ({ ...n, is_read: true })));
                setUnreadCount(0);
            }
        } catch (error) {
            console.error('Failed to mark all read', error);
        }
    };

    const handleAction = (notification) => {
        if (!notification.is_read) {
            // Mark as read when clicking action
            NotificationService.markAsRead(notification.id);
        }

        if (notification.action_url) {
            // Convert server URL to client route if possible
            // Most server URLs are like /tasks or /inventory
            const clientPath = notification.action_url;
            navigate(clientPath);
        }
    };

    const getIcon = (type) => {
        switch (type) {
            case 'TASK_OVERDUE':
            case 'WARRANTY_EXPIRING':
                return <AlertTriangle className="text-red-500" size={20} />;
            case 'TASK_DUE_SOON':
                return <Calendar className="text-orange-500" size={20} />;
            case 'TASK_COMPLETED':
                return <CheckCircle2 className="text-green-500" size={20} />;
            case 'RECURRING_TASK':
                return <CheckSquare className="text-blue-500" size={20} />;
            default:
                return <Info className="text-blue-500" size={20} />;
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'HIGH': return 'bg-red-50 border-red-100';
            case 'MEDIUM': return 'bg-orange-50 border-orange-100';
            case 'LOW': return 'bg-blue-50 border-blue-100';
            default: return 'bg-[var(--bg-card)] border-[var(--border)]';
        }
    };

    return (
        <div className="max-w-4xl mx-auto p-4 md:p-6 pb-24">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 animate-in fade-in slide-in-from-top-4 duration-500">
                <div>
                    <h1 className="text-3xl font-bold text-[var(--text-main)] mb-1">Notifications</h1>
                    <p className="text-[var(--text-secondary)]">
                        You have {unreadCount} unread notifications
                    </p>
                </div>

                <div className="flex items-center gap-2">
                    <div className="bg-[var(--bg-card)] p-1 rounded-lg border border-[var(--border)] flex">
                        <button
                            onClick={() => setFilter('all')}
                            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${filter === 'all'
                                ? 'bg-[var(--primary)] text-white shadow-sm'
                                : 'text-[var(--text-secondary)] hover:text-[var(--text-main)]'
                                }`}
                        >
                            All
                        </button>
                        <button
                            onClick={() => setFilter('unread')}
                            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${filter === 'unread'
                                ? 'bg-[var(--primary)] text-white shadow-sm'
                                : 'text-[var(--text-secondary)] hover:text-[var(--text-main)]'
                                }`}
                        >
                            Unread
                        </button>
                    </div>

                    {unreadCount > 0 && (
                        <button
                            onClick={handleMarkAllRead}
                            className="flex items-center gap-2 px-4 py-2 bg-[var(--bg-card)] hover:bg-[var(--bg-hover)] text-[var(--text-main)] border border-[var(--border)] rounded-xl transition-all shadow-sm"
                        >
                            <Check size={18} />
                            <span className="hidden sm:inline">Mark all read</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Notifications List */}
            <div className="space-y-4">
                {loading ? (
                    <div className="text-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--primary)] mx-auto mb-4"></div>
                        <p className="text-[var(--text-secondary)]">Loading notifications...</p>
                    </div>
                ) : notifications.length === 0 ? (
                    <div className="text-center py-16 bg-[var(--bg-card)] rounded-2xl border border-[var(--border)] border-dashed">
                        <div className="w-16 h-16 bg-[var(--bg-hover)] rounded-full flex items-center justify-center mx-auto mb-4 text-[var(--text-secondary)]">
                            <Bell size={32} />
                        </div>
                        <h3 className="text-lg font-medium text-[var(--text-main)] mb-1">No notifications</h3>
                        <p className="text-[var(--text-secondary)]">You&apos;re all caught up!</p>
                    </div>
                ) : (
                    <AnimatePresence>
                        {notifications.map((notification) => (
                            <motion.div
                                key={notification.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                className={`
                                    group relative p-4 rounded-xl border transition-all duration-200 hover:shadow-md
                                    ${notification.is_read
                                        ? 'bg-[var(--bg-card)] border-[var(--border)] opacity-75 hover:opacity-100'
                                        : getPriorityColor(notification.priority) + ' shadow-sm'
                                    }
                                `}
                                onClick={() => handleAction(notification)}
                            >
                                <div className="flex gap-4">
                                    <div className={`
                                        flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center
                                        ${notification.is_read ? 'bg-[var(--bg-hover)]' : 'bg-white shadow-sm'}
                                    `}>
                                        {getIcon(notification.type)}
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <div className="flex justify-between items-start mb-1">
                                            <h3 className={`font-semibold text-lg truncate pr-8 ${notification.is_read ? 'text-[var(--text-secondary)]' : 'text-[var(--text-main)]'}`}>
                                                {notification.title}
                                            </h3>
                                            <span className="text-xs text-[var(--text-secondary)] whitespace-nowrap">
                                                {new Date(notification.created_at).toLocaleDateString()}
                                            </span>
                                        </div>

                                        <p className={`text-sm mb-3 ${notification.is_read ? 'text-[var(--text-muted)]' : 'text-[var(--text-secondary)]'}`}>
                                            {notification.message}
                                        </p>

                                        {/* Actions */}
                                        <div className="flex items-center gap-3">
                                            {notification.action_url && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleAction(notification);
                                                    }}
                                                    className="text-sm font-medium text-[var(--primary)] hover:underline flex items-center gap-1"
                                                >
                                                    {notification.action_text || 'View details'} <ArrowRight size={14} />
                                                </button>
                                            )}

                                            {!notification.is_read && (
                                                <button
                                                    onClick={(e) => handleMarkRead(notification.id, e)}
                                                    className="text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--text-main)] flex items-center gap-1"
                                                >
                                                    <Check size={14} /> Mark as read
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Hover Actions (Absolute) */}
                                    <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                            onClick={(e) => handleDismiss(notification.id, e)}
                                            className="p-1 text-[var(--text-muted)] hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                            title="Dismiss"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>

                                {/* Unread Indicator */}
                                {!notification.is_read && (
                                    <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-[var(--primary)] group-hover:opacity-0 transition-opacity" />
                                )}
                            </motion.div>
                        ))}
                    </AnimatePresence>
                )}
            </div>
        </div>
    );
};

export default Notifications;
